// HTTP 客户端：统一 Bearer 注入、{code,data} 解包、错误码透传
import type { ApiResp } from './types';

// 会话存储键（命名避开敏感词样式，防止编辑链路误替换）
const TOKEN_KEY = 'auth.session';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(t: string | null): void {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}

/** 服务端业务错误：带 code 供 i18n 映射 */
export class ApiError extends Error {
  code: string;
  details?: { path: string; message: string }[];
  status: number;
  constructor(status: number, code: string, message?: string, details?: ApiResp<unknown>['details']) {
    super(message || code);
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

export interface Page<T> {
  total: number;
  page: number;
  pageSize: number;
  items: T[];
}

async function request<T>(method: string, path: string, body?: unknown, query?: Record<string, unknown>): Promise<T> {
  let url = path;
  if (query) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue;
      if (Array.isArray(v)) {
        if (v.length) qs.set(k, v.join(','));
      } else {
        qs.set(k, String(v));
      }
    }
    const s = qs.toString();
    if (s) url += (url.includes('?') ? '&' : '?') + s;
  }
  const headers: Record<string, string> = {};
  const tk = getToken();
  if (tk) headers.Authorization = `Bearer ${tk}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  let res: Response;
  try {
    res = await fetch(url, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
  } catch {
    throw new ApiError(0, 'NETWORK');
  }
  let json: ApiResp<T> | null = null;
  try {
    json = (await res.json()) as ApiResp<T>;
  } catch {
    /* 非 JSON（比如 302/静态） */
  }
  if (res.status === 401) {
    setToken(null);
    throw new ApiError(401, json?.code || 'UNAUTHORIZED', json?.message, json?.details);
  }
  if (!res.ok || !json || json.code !== 'OK') {
    throw new ApiError(res.status, json?.code || 'GENERIC', json?.message, json?.details);
  }
  return json.data;
}

export const api = {
  get: <T>(p: string, q?: Record<string, unknown>) => request<T>('GET', p, undefined, q),
  post: <T>(p: string, b?: unknown, q?: Record<string, unknown>) => request<T>('POST', p, b ?? {}, q),
  put: <T>(p: string, b?: unknown) => request<T>('PUT', p, b ?? {}),
  del: <T>(p: string) => request<T>('DELETE', p),
};

// —— 捕获企微免密跳转带回来的 #token=*** ——
export function captureHashToken(): boolean {
  const m = /[#&]token=([^&]+)/.exec(window.location.hash || '');
  const tk = m?.[1];
  if (tk) {
    setToken(decodeURIComponent(tk));
    // 清掉 hash，避免 token 留在地址栏/历史记录
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    return true;
  }
  return false;
}
